import { notification } from "antd";
import type { NotificationInstance } from "antd/es/notification/interface";
import i18n from "@/i18n";
import { resolveNotificationCopy } from "../model/copy";
import type { Notification } from "../model/types";

// Set by the app's RealtimeToastHost: a hook-based instance whose toasts stack.
// The static API is the fallback (tests, or a toast before the host mounts),
// and it can't stack below its fixed threshold of three.
let toastApi: NotificationInstance | null = null;

export function setRealtimeToastApi(api: NotificationInstance | null) {
  toastApi = api;
}

/** Stack from the second toast; hovering the stack fans it out. */
export const REALTIME_TOAST_CONFIG = {
  placement: "topRight",
  maxCount: 3,
  stack: { threshold: 1 },
} as const;

/**
 * Toast for a notification arriving over the realtime stream. Renders the
 * SAME translated copy as the notification center (locale templates at
 * `notifications.copy.<TYPE>`), falling back to the backend title/body for
 * unknown types — the raw backend sentence must never be the first choice
 * (e.g. publishing used to toast "A new change has been pushed to your
 * StoreMandu website." instead of the dashboard's wording).
 */
export function showNewNotificationToast(notificationData: unknown) {
  if (!notificationData || typeof notificationData !== "object") return;

  const n = notificationData as Notification;
  const { title, body } = resolveNotificationCopy(
    n,
    i18n.t.bind(i18n) as (
      key: string,
      options?: Record<string, unknown>,
    ) => string,
  );
  const notificationType = n.data?.notificationType || "INFO";

  // AntD draws the type's icon itself, so the title carries no emoji.
  let method: "success" | "warning" | "error" | "info";
  let style = {};

  switch (notificationType) {
    case "SUCCESS":
      method = "success";
      style = { borderLeft: "4px solid #52c41a" };
      break;
    case "WARNING":
      method = "warning";
      style = { borderLeft: "4px solid #faad14" };
      break;
    case "ERROR":
      method = "error";
      style = { borderLeft: "4px solid #f5222d" };
      break;
    case "INFO":
    default:
      method = "info";
      style = { borderLeft: "4px solid #1890ff" };
      break;
  }

  const api = toastApi ?? notification;
  const key = `realtime-${n.id ?? Date.now()}`;
  api[method]({
    key,
    message: title || "New Notification",
    description: body || "You have a new notification",
    placement: "topRight",
    duration: 4.5,
    style: {
      ...style,
      borderRadius: 8,
      padding: "12px 16px",
      maxWidth: 400,
    },
    onClick: () => {
      // Close this toast when clicked
      api.destroy(key);
    },
  });
}
